// src/utils/educationAnalyzer.js
// 🧠 教育ニーズ分析エンジン - 包括的教育配置システムの中核

import { 
    analyzeIndividualEducationNeed,
    calculateInstructionCapability,
    calculateLearningPotential,
    calculateEducationValue,
    isTimeOverlap,
    getPriorityValue,
    EDUCATION_TYPES,
    EDUCATION_PRIORITIES,
    SKILL_THRESHOLDS,
    isComplexProcedure,
    isSimpleProcedure,
    isRareProcedure,
    isInnovativeProcedure
  } from "./educationUtils";
  
  /**
   * 🎓 包括的教育ニーズ分析（メイン関数）
   * @param {Object} basicAssignments - 基本配置結果
   * @param {Array} allStaff - 全スタッフリスト
   * @returns {Array} 教育ニーズリスト
   */
  export const analyzeComprehensiveEducationNeeds = async (basicAssignments, allStaff) => {
    const educationNeeds = [];
    
    console.log("🔍 包括的教育ニーズ分析開始...");
    
    // STEP 1: 基本配置されたスタッフの教育ニーズ分析
    for (const [surgeryId, assignment] of Object.entries(basicAssignments)) {
      const surgery = assignment.surgery;
      
      for (const role of ["scrub", "circulating"]) {
        const staff = assignment[role];
        if (!staff) continue;
        
        const needsAnalysis = await analyzeIndividualEducationNeed(staff, surgery, role);
        
        if (needsAnalysis.needsEducation) {
          educationNeeds.push({
            surgeryId,
            surgery,
            targetStaff: staff,
            role,
            ...needsAnalysis,
            educationOpportunityScore: calculateEducationOpportunityScore(needsAnalysis, surgery)
          });
        }
      }
    }
    
    // STEP 2: 手術自体の教育価値分析（見学・学習機会として）
    for (const [surgeryId, assignment] of Object.entries(basicAssignments)) {
      const surgery = assignment.surgery;
      const surgeryEducationValue = calculateSurgeryEducationValue(surgery);
      
      if (surgeryEducationValue.hasEducationValue) {
        educationNeeds.push({
          surgeryId,
          surgery,
          targetStaff: null, // 未配置
          role: "educational_opportunity",
          educationType: "general_learning",
          priority: surgeryEducationValue.priority,
          educationOpportunityScore: surgeryEducationValue.score,
          learningObjectives: surgeryEducationValue.objectives
        });
      }
    }
    
    // 教育機会スコアでソート
    educationNeeds.sort((a, b) => b.educationOpportunityScore - a.educationOpportunityScore);
    
    console.log(`✅ 教育ニーズ分析完了: ${educationNeeds.length}件の教育機会を特定`);
    return educationNeeds;
  };
  
  /**
   * 📊 教育機会スコアの計算
   */
  const calculateEducationOpportunityScore = (needsAnalysis, surgery) => {
    let score = 50; // ベーススコア
    
    // 優先度による加点
    const priorityBonus = {
      [EDUCATION_PRIORITIES.HIGH]: 30,
      [EDUCATION_PRIORITIES.MEDIUM]: 15,
      [EDUCATION_PRIORITIES.LOW]: 5
    };
    score += priorityBonus[needsAnalysis.priority] || 0;
    
    // スキルギャップによる加点
    if (needsAnalysis.skillGap) {
      score += Math.min(25, needsAnalysis.skillGap * 8);
    }
    
    // 手術の教育価値による加点
    if (isComplexProcedure(surgery.procedure)) {
      score += 20;
    } else if (isRareProcedure(surgery.procedure)) {
      score += 25;
    }
    
    // 緊急度による加点
    if (needsAnalysis.urgencyScore) {
      score += Math.min(15, needsAnalysis.urgencyScore * 0.15);
    }
    
    return Math.max(0, Math.min(100, score));
  };
  
  /**
   * 🏥 手術の教育価値計算
   */
  const calculateSurgeryEducationValue = (surgery) => {
    let score = 0;
    const objectives = [];
    
    const procedure = surgery.procedure || "";
    const department = surgery.department || "";
    
    // 稀少手術は高い教育価値
    if (isRareProcedure(procedure)) {
      score += 40;
      objectives.push("稀少手術の見学");
    }
    
    // 高難度手術は教育価値大
    if (isComplexProcedure(procedure)) {
      score += 30;
      objectives.push("高難度手術の学習");
    }
    
    // 教育に適した手術時間
    const duration = calculateSurgeryDuration(surgery);
    if (duration >= 120 && duration <= 300) { // 2-5時間
      score += 20;
      objectives.push("十分な学習時間の確保");
    }
    
    // 特定診療科の教育価値
    const highEducationDepts = ["心臓外科", "脳神経外科", "整形外科"];
    if (highEducationDepts.includes(department)) {
      score += 25;
      objectives.push(`${department}専門技術の習得`);
    }
    
    // 新しい技術・手法
    if (isInnovativeProcedure(procedure)) {
      score += 35;
      objectives.push("最新技術の習得");
    }
    
    const hasEducationValue = score >= 30;
    const priority = score >= 60 ? EDUCATION_PRIORITIES.HIGH : 
                    score >= 40 ? EDUCATION_PRIORITIES.MEDIUM : EDUCATION_PRIORITIES.LOW;
    
    return {
      hasEducationValue,
      score,
      priority,
      objectives
    };
  };
  
  /**
   * 🎯 教育役割の分類（拡張版）
   * @param {Array} availableStaff - 利用可能スタッフ
   * @param {Array} educationNeeds - 教育ニーズ
   * @returns {Object} 分類されたスタッフ
   */
  export const classifyEducationalRoles = async (availableStaff, educationNeeds) => {
    const instructors = [];
    const learners = [];
    const multipurpose = []; // 指導も学習も可能
    
    console.log(`👥 教育役割分類開始: ${availableStaff.length}人を分析`);
    
    for (const staff of availableStaff) {
      const instructionCapability = calculateInstructionCapability(staff);
      const learningPotential = calculateLearningPotential(staff, educationNeeds);
      
      if (instructionCapability.canInstruct && learningPotential.canLearn) {
        multipurpose.push({
          ...staff,
          instructionCapability,
          learningPotential,
          flexibility: calculateFlexibility(instructionCapability, learningPotential)
        });
      } else if (instructionCapability.canInstruct) {
        instructors.push({
          ...staff,
          instructionCapability,
          specialties: instructionCapability.specialties
        });
      } else if (learningPotential.canLearn) {
        learners.push({
          ...staff,
          learningPotential,
          priorityAreas: learningPotential.priorityAreas
        });
      }
    }
    
    // ソート
    instructors.sort((a, b) => b.instructionCapability.score - a.instructionCapability.score);
    learners.sort((a, b) => b.learningPotential.score - a.learningPotential.score);
    multipurpose.sort((a, b) => b.flexibility - a.flexibility);
    
    console.log(`✅ 分類完了: 指導者=${instructors.length}人, 学習者=${learners.length}人, 多目的=${multipurpose.length}人`);
    
    return { instructors, learners, multipurpose };
  };
  
  /**
   * 🔄 柔軟性計算（多目的スタッフ用）
   */
  const calculateFlexibility = (instructionCapability, learningPotential) => {
    const instructionScore = instructionCapability.score || 0;
    const learningScore = learningPotential.score || 0;
    
    // バランスが良いほど柔軟性が高い
    const balance = 100 - Math.abs(instructionScore - learningScore);
    const average = (instructionScore + learningScore) / 2;
    
    return (balance + average) / 2;
  };
  
  /**
   * 🎓 教育スタッフの配置（メイン処理）
   * @param {Array} educationNeeds - 教育ニーズ
   * @param {Object} educationRoles - 分類された教育役割
   * @param {Object} basicAssignments - 基本配置
   * @param {Array} eventParticipants - イベント参加者
   * @param {string} date - 対象日
   * @returns {Object} 配置結果
   */
  export const assignEducationalStaff = async (educationNeeds, educationRoles, basicAssignments, eventParticipants, date) => {
    const assignments = {};
    const unusedInstructors = [...educationRoles.instructors, ...educationRoles.multipurpose];
    const unusedLearners = [...educationRoles.learners, ...educationRoles.multipurpose];
    const warnings = [];
    
    console.log(`🎯 教育配置開始: ${educationNeeds.length}件のニーズ処理`);
    
    for (const need of educationNeeds) {
      const assignment = await assignEducationForNeed(
        need,
        unusedInstructors,
        unusedLearners,
        eventParticipants,
        date
      );
      
      if (assignment.success) {
        assignments[need.surgeryId] = {
          ...basicAssignments[need.surgeryId],
          educationAssignment: assignment.educationAssignment
        };
        
        // 使用済みスタッフを除去
        if (assignment.instructorUsed) {
          const index = unusedInstructors.findIndex(i => i.employeeId === assignment.instructorUsed.employeeId);
          if (index !== -1) unusedInstructors.splice(index, 1);
        }
        
        if (assignment.learnerAssigned) {
          const index = unusedLearners.findIndex(l => l.employeeId === assignment.learnerAssigned.employeeId);
          if (index !== -1) unusedLearners.splice(index, 1);
        }
        
        console.log(`✅ ${need.surgery.procedure} (${need.role}): ${assignment.educationAssignment.type}配置完了`);
      } else {
        warnings.push({
          type: "education_assignment_failed",
          surgeryId: need.surgeryId,
          reason: assignment.reason,
          message: `${need.surgery.procedure}の教育配置に失敗: ${assignment.reason}`
        });
      }
    }
    
    return {
      assignments,
      unusedStaff: [...new Set([...unusedInstructors, ...unusedLearners])], // 重複除去
      warnings
    };
  };
  
  /**
   * 🎯 個別教育ニーズに対する配置
   */
  const assignEducationForNeed = async (need, availableInstructors, availableLearners, eventParticipants, date) => {
    const surgery = need.surgery;
    const surgeryStart = surgery.start;
    const surgeryEnd = surgery.end;
    
    // 教育タイプに応じた配置戦略
    switch (need.educationType) {
      case EDUCATION_TYPES.FIRST_OBSERVATION:
        return assignObservationLearner(need, availableLearners, eventParticipants, surgeryStart, surgeryEnd);
        
      case EDUCATION_TYPES.GUIDED_PRACTICE:
        return assignGuidedPractice(need, availableInstructors, eventParticipants, surgeryStart, surgeryEnd);
        
      case EDUCATION_TYPES.SUPERVISED_INDEPENDENCE:
        return assignSupervision(need, availableInstructors, eventParticipants, surgeryStart, surgeryEnd);
        
      case EDUCATION_TYPES.FINAL_ASSESSMENT:
        return assignFinalAssessment(need, availableInstructors, eventParticipants, surgeryStart, surgeryEnd);
        
      case "general_learning":
        return assignGeneralLearning(need, availableLearners, eventParticipants, surgeryStart, surgeryEnd);
        
      default:
        return { success: false, reason: "未対応の教育タイプ" };
    }
  };
  
  /**
   * 👀 見学者配置
   */
  const assignObservationLearner = async (need, availableLearners, eventParticipants, surgeryStart, surgeryEnd) => {
    const suitableLearners = availableLearners.filter(learner => {
      // 時間制約チェック
      const hasTimeConflict = eventParticipants.some(event => {
        if (event.employeeId !== learner.employeeId) return false;
        return isTimeOverlap(surgeryStart, surgeryEnd, event.startTime, event.endTime);
      });
      
      if (hasTimeConflict) return false;
      
      // 学習エリアの適合性チェック
      const targetArea = `${need.surgery.department}_${need.role}`;
      const learningPotential = learner.learningPotential || {};
      const priorityAreas = learningPotential.priorityAreas || [];
      
      return priorityAreas.some(area => area.area === targetArea) || 
             priorityAreas.some(area => area.area === "basic_skills");
    });
    
    if (suitableLearners.length === 0) {
      return { success: false, reason: "適切な見学者が見つかりません" };
    }
    
    const selectedLearner = suitableLearners[0];
    
    return {
      success: true,
      educationAssignment: {
        type: EDUCATION_TYPES.FIRST_OBSERVATION,
        observer: selectedLearner,
        educationGoal: `${need.surgery.procedure}の${need.role === "scrub" ? "器械出し" : "外回り"}見学`,
        expectedOutcome: "基本的な流れの理解",
        learningFocus: generateLearningFocus(selectedLearner, need.surgery)
      },
      learnerAssigned: selectedLearner
    };
  };
  
  /**
   * 👨‍🏫 指導下実践配置
   */
  const assignGuidedPractice = async (need, availableInstructors, eventParticipants, surgeryStart, surgeryEnd) => {
    const suitableInstructors = availableInstructors.filter(instructor => {
      // 時間制約チェック
      const hasTimeConflict = eventParticipants.some(event => {
        if (event.employeeId !== instructor.employeeId) return false;
        return isTimeOverlap(surgeryStart, surgeryEnd, event.startTime, event.endTime);
      });
      
      if (hasTimeConflict) return false;
      
      // 指導能力チェック
      const instructorSkills = instructor.skills?.departments?.[need.surgery.department];
      if (!instructorSkills) return false;
      
      const requiredSkill = need.role === "scrub" 
        ? instructorSkills.scrubScore 
        : instructorSkills.circulatingScore;
      
      return (requiredSkill || 0) >= SKILL_THRESHOLDS.ADVANCED_MIN;
    });
    
    if (suitableInstructors.length === 0) {
      return { success: false, reason: "適切な指導者が見つかりません" };
    }
    
    const selectedInstructor = suitableInstructors[0];
    
    return {
      success: true,
      educationAssignment: {
        type: EDUCATION_TYPES.GUIDED_PRACTICE,
        instructor: selectedInstructor,
        trainee: need.targetStaff,
        educationGoal: `${need.surgery.procedure}の${need.role === "scrub" ? "器械出し" : "外回り"}指導下実践`,
        expectedOutcome: "手技の習得と理解度向上",
        instructionPlan: generateInstructionPlan(need.educationPlan)
      },
      instructorUsed: selectedInstructor
    };
  };
  
  /**
   * 👀 監督下独立配置
   */
  const assignSupervision = async (need, availableInstructors, eventParticipants, surgeryStart, surgeryEnd) => {
    const suitableSupervisors = availableInstructors.filter(instructor => {
      const hasTimeConflict = eventParticipants.some(event => {
        if (event.employeeId !== instructor.employeeId) return false;
        return isTimeOverlap(surgeryStart, surgeryEnd, event.startTime, event.endTime);
      });
      
      const instructionCapability = instructor.instructionCapability || {};
      return !hasTimeConflict && instructionCapability.score >= 60;
    });
    
    if (suitableSupervisors.length === 0) {
      return { success: false, reason: "適切な監督者が見つかりません" };
    }
    
    const selectedSupervisor = suitableSupervisors[0];
    
    return {
      success: true,
      educationAssignment: {
        type: EDUCATION_TYPES.SUPERVISED_INDEPENDENCE,
        supervisor: selectedSupervisor,
        supervisee: need.targetStaff,
        educationGoal: `${need.surgery.procedure}の監督下独立実践`,
        expectedOutcome: "独立実践能力の確認",
        supervisionLevel: determinateSupervisionLevel(need.currentLevel)
      },
      instructorUsed: selectedSupervisor
    };
  };
  
  /**
   * 📝 最終評価配置
   */
  const assignFinalAssessment = async (need, availableInstructors, eventParticipants, surgeryStart, surgeryEnd) => {
    const suitableEvaluators = availableInstructors.filter(instructor => {
      const hasTimeConflict = eventParticipants.some(event => {
        if (event.employeeId !== instructor.employeeId) return false;
        return isTimeOverlap(surgeryStart, surgeryEnd, event.startTime, event.endTime);
      });
      
      const instructionCapability = instructor.instructionCapability || {};
      return !hasTimeConflict && 
             instructionCapability.score >= 70 &&
             (instructor.experienceYears || 0) >= 5;
    });
    
    if (suitableEvaluators.length === 0) {
      return { success: false, reason: "適切な評価者が見つかりません" };
    }
    
    const selectedEvaluator = suitableEvaluators[0];
    
    return {
      success: true,
      educationAssignment: {
        type: EDUCATION_TYPES.FINAL_ASSESSMENT,
        evaluator: selectedEvaluator,
        candidate: need.targetStaff,
        educationGoal: `${need.surgery.procedure}の独り立ち評価`,
        expectedOutcome: "独り立ち可否の判定",
        assessmentCriteria: need.educationPlan?.evaluationCriteria || []
      },
      instructorUsed: selectedEvaluator
    };
  };
  
  /**
   * 📚 一般学習配置
   */
  const assignGeneralLearning = async (need, availableLearners, eventParticipants, surgeryStart, surgeryEnd) => {
    const suitableLearners = availableLearners.filter(learner => {
      const hasTimeConflict = eventParticipants.some(event => {
        if (event.employeeId !== learner.employeeId) return false;
        return isTimeOverlap(surgeryStart, surgeryEnd, event.startTime, event.endTime);
      });
      
      return !hasTimeConflict;
    });
    
    if (suitableLearners.length === 0) {
      return { success: false, reason: "適切な学習者が見つかりません" };
    }
    
    // 教育価値の高い学習者を選択
    const learnerWithValue = suitableLearners.map(learner => ({
      ...learner,
      educationValue: calculateEducationValue(learner, [need.surgery])
    })).sort((a, b) => b.educationValue - a.educationValue);
    
    const selectedLearner = learnerWithValue[0];
    
    return {
      success: true,
      educationAssignment: {
        type: EDUCATION_TYPES.ADDITIONAL_OBSERVATION,
        observer: selectedLearner,
        educationGoal: need.learningObjectives?.join(", ") || "一般的な学習",
        expectedOutcome: "専門知識と技術の向上",
        learningFocus: need.learningObjectives || ["手術見学", "技術習得"]
      },
      learnerAssigned: selectedLearner
    };
  };
  
  /**
   * 🔄 補助関数群
   */
  const calculateSurgeryDuration = (surgery) => {
    if (!surgery.start || !surgery.end) return 60;
    
    const start = new Date(`2024-01-01T${surgery.start}`);
    const end = new Date(`2024-01-01T${surgery.end}`);
    
    return (end - start) / (1000 * 60); // 分単位
  };
  
  const generateLearningFocus = (learner, surgery) => {
    const focus = [`${surgery.procedure}の流れ`, "使用器械", "看護師の役割"];
    
    const experienceYears = learner.experienceYears || 0;
    if (experienceYears <= 1) {
      focus.push("基本的な手術室のルール");
    }
    
    return focus;
  };
  
  const generateInstructionPlan = (educationPlan) => {
    if (!educationPlan) return ["基本指導"];
    
    return educationPlan.educationSteps?.map(step => step.description) || ["段階的指導"];
  };
  
  const determinateSupervisionLevel = (currentLevel) => {
    if (currentLevel <= 3) return "密接な監督";
    else if (currentLevel <= 4) return "定期的な確認";
    else return "最小限の監督";
  };
  
  export default {
    analyzeComprehensiveEducationNeeds,
    classifyEducationalRoles,
    assignEducationalStaff
  };